import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import Modal from "@via/components/modal.tsx";
import Table from "@via/components/table/table.tsx";
import { EMPTY_TABLE_MESSAGE, ModalSize } from "@via/utils/constants.tsx";
import {
  useDeploymentConfig,
  useDeployAIModel,
} from "@via/pages/cortex/model-garden/model-garden.api.tsx";
import { DeployConfig, VertexAIModelDeploymentConfig } from "common";
import { type FC, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Tooltip } from "react-tooltip";

interface DeployFormValues {
  endpointDisplayName: string;
}

interface DeployModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  aiModelId: number | undefined;
}

const columnHelper = createColumnHelper<DeployConfig>();

const DeployModal: FC<DeployModalProps> = ({ open, setOpen, aiModelId }) => {
  const [selectedConfig, setSelectedConfig] = useState<DeployConfig | null>(
    null
  );

  const { control, handleSubmit, reset, watch } = useForm<DeployFormValues>({
    mode: "onChange",
    defaultValues: {
      endpointDisplayName: "",
    },
  });

  const { data: deploymentConfigData, isLoading: isConfigLoading } =
    useDeploymentConfig(aiModelId, open && aiModelId !== undefined);

  const { mutateAsync: deployAIModel, isPending: isDeployingLoading } =
    useDeployAIModel();

  const deploymentConfigs = deploymentConfigData?.deploymentConfigs || [];

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open) {
      reset({ endpointDisplayName: "" });
      setSelectedConfig(null);
    }
  }, [open, reset]);

  // Auto-select first config when data loads
  useEffect(() => {
    if (deploymentConfigs.length > 0 && !selectedConfig) {
      setSelectedConfig(deploymentConfigs[0]);
    }
  }, [deploymentConfigs, selectedConfig]);

  // Reports back to Modal, which owns the close: false keeps the dialog open on invalid input, a
  // failed deploy rejects (handleSubmit rethrows) and also keeps it open, true closes it.
  const handleConfirmModal = async (): Promise<boolean> => {
    let deployed = false;
    await handleSubmit(async (data: DeployFormValues): Promise<void> => {
      if (!aiModelId || !selectedConfig || !data.endpointDisplayName.trim()) {
        return;
      }

      await deployAIModel({
        id: aiModelId,
        payload: {
          endpointDisplayName: data.endpointDisplayName.trim(),
          deploymentConfig: selectedConfig,
        },
      });
      deployed = true;
    })();
    return deployed;
  };

  const isConfigSelected = (config: DeployConfig): boolean => {
    return (
      selectedConfig?.machineType === config.machineType &&
      selectedConfig?.acceleratorType === config.acceleratorType &&
      selectedConfig?.acceleratorCount === config.acceleratorCount &&
      selectedConfig?.containerImageURI === config.containerImageURI
    );
  };

  const columns = useMemo(
    () =>
      [
        columnHelper.display({
          id: "select",
          cell: info => {
            const isSelected = isConfigSelected(info.row.original);
            return (
              <div className="flex items-center justify-center">
                <input
                  type="radio"
                  name="deploymentConfig"
                  checked={isSelected}
                  onChange={() => setSelectedConfig(info.row.original)}
                  className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600 cursor-pointer"
                />
              </div>
            );
          },
          header: () => "",
          enableSorting: false,
          size: 50,
          minSize: 50,
          maxSize: 50,
        }),
        columnHelper.accessor("machineType", {
          cell: info => {
            const value = info.getValue();
            if (!value) return null;
            return (
              <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                {value}
              </span>
            );
          },
          header: () => <span>Machine Type</span>,
          enableSorting: false,
        }),
        columnHelper.accessor("acceleratorType", {
          cell: info => {
            const value = info.getValue();
            if (!value) return null;
            return (
              <span className="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-700/10">
                {value}
              </span>
            );
          },
          header: () => <span>Accelerator Type</span>,
          enableSorting: false,
          size: 150,
        }),
        columnHelper.accessor("acceleratorCount", {
          cell: info => {
            const value = info.getValue();
            if (value === undefined || value === null) return null;
            return (
              <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-700/10">
                {value}
              </span>
            );
          },
          header: () => <span>Accelerator Count</span>,
          enableSorting: false,
          size: 50,
        }),
        columnHelper.accessor("containerImageURI", {
          cell: info => {
            const value = info.getValue();
            if (!value) return null;
            const rowIndex = info.row.index;
            return (
              <>
                <span
                  id={`container-image-${rowIndex}`}
                  className="block max-w-[180px] truncate cursor-help">
                  {value}
                </span>
                <Tooltip
                  anchorSelect={`#container-image-${rowIndex}`}
                  content={value}
                  place="top"
                  className="z-50"
                />
              </>
            );
          },
          header: () => <span>Container Image</span>,
          enableSorting: false,
          size: 200,
        }),
      ] as Array<ColumnDef<VertexAIModelDeploymentConfig>>,
    [selectedConfig]
  );

  const isConfirmDisabled =
    !watch("endpointDisplayName")?.trim() || !selectedConfig || isConfigLoading;

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      size={ModalSize.Large}
      width={800}
      title="Deploy AI Model"
      confirmText="Deploy"
      isConfirmLoading={isDeployingLoading}
      isConfirmDisabled={isConfirmDisabled}
      isLoading={isConfigLoading}
      onConfirm={handleConfirmModal}>
      <form onSubmit={e => e.preventDefault()}>
        <div className="flex flex-col gap-6 py-4">
          {/* Endpoint Name Input */}
          <FormInputComponent
            control={control}
            name="endpointDisplayName"
            label="Endpoint Name"
            placeholder="Enter a display name for the endpoint"
            showRequiredField={true}
            validations={{
              required: "Endpoint name is required",
            }}
            showTooltipIcon={true}
            tooltipText="This name will be used to identify your deployed endpoint."
          />

          {/* Deployment Configuration Selection */}
          <div>
            <label className="block text-sm font-medium leading-6 text-gray-900">
              Deployment Configuration <span className="text-red-500">*</span>
            </label>
            <Table
              isLoading={isConfigLoading}
              data={deploymentConfigs}
              columns={columns}
              showPagination={false}
              enableSorting={false}
              emptyMessage={EMPTY_TABLE_MESSAGE.DEPLOYMENT_CONFIGS}
              onRowClick={row => setSelectedConfig(row)}
              applyColumnSize={true}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default DeployModal;
